import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, Leaf, MessagesSquare } from "lucide-react";
import { useLang } from "./ctx";
import { MaskLines, Reveal, Label } from "./Motion";

const ICONS = [ShieldCheck, Leaf, MessagesSquare];

export const About = () => {
  const { t, lang } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="about" ref={ref} className="relative py-20 sm:py-28 lg:py-40" data-testid="about-section">
      <div className="mx-auto max-w-[1440px] px-6 lg:px-12">
        <Label>{t.about.label}</Label>
        <h2 key={lang} className="mt-8 max-w-5xl font-display text-4xl font-light leading-[1.02] tracking-tight text-stone-950 sm:text-6xl lg:text-7xl">
          <MaskLines lines={t.about.title.slice(0, 1)} />
          <MaskLines lines={t.about.title.slice(1, 2)} delay={0.11} lineClass="italic text-[#A8874F]" />
          <MaskLines lines={t.about.title.slice(2)} delay={0.22} />
        </h2>
        <div className="mt-12 grid gap-10 sm:mt-20 lg:grid-cols-12 lg:gap-16">
          <Reveal className="lg:col-span-5">
            <div className="relative aspect-[4/5] overflow-hidden rounded-sm bg-stone-200 [clip-path:polygon(0_0,100%_0,100%_88%,88%_100%,0_100%)]">
              <motion.img style={{ y, scale: 1.18 }} src="/assets/machinery.jpg" alt="Saha" className="h-full w-full object-cover" />
            </div>
          </Reveal>
          <div className="lg:col-span-7 lg:pt-10">
            <Reveal><p data-testid="about-text-1" className="font-display text-xl leading-snug text-stone-900 sm:text-3xl">{t.about.p1}</p></Reveal>
            <Reveal delay={0.1}><p data-testid="about-text-2" className="mt-8 max-w-2xl text-base leading-relaxed text-stone-600 md:text-lg">{t.about.p2}</p></Reveal>
            <div className="mt-12 grid gap-px sm:mt-16 overflow-hidden rounded-sm border border-stone-200 bg-stone-200 sm:grid-cols-3">
              {t.about.pillars.map((p, i) => {
                const Icon = ICONS[i];
                return (
                  <Reveal key={p.t} delay={0.1 * i} className="group h-full bg-[#FBFAF7] p-6 transition-colors duration-500 hover:bg-white sm:p-7" >
                    <div data-testid={`about-pillar-${i}`}>
                      <Icon size={22} strokeWidth={1.25} className="text-[#A8874F] transition-transform duration-500 group-hover:-translate-y-1" />
                      <div className="mt-6 text-sm font-medium tracking-wide text-stone-900">{p.t}</div>
                      <div className="mt-2 text-sm leading-relaxed text-stone-600">{p.d}</div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
